import { lazy, useCallback } from "react";

import {
  NavigateOptions,
  Route,
  Routes,
  useNavigate,
  useParams,
} from "react-router-dom";

import { NoMatchError, UnauthorizedError } from "@/components/errors";

import { __GLOBAL_ASSESSMENT_ID, __GLOBAL_TENANT } from "./client/globals";
import { FeedbackSummaryPage } from "./features/home/pages/feedback-summary-page";
import { PeerOverviewPageWithData } from "./features/home/pages/peer-overview-page";
import { Preparation } from "./features/home/pages/preparation";
import { ProjectManagementPage } from "./features/home/pages/project-management";
import { StudentInsights } from "./features/home/pages/student-insights";
import { WritingPage } from "./features/home/pages/writing-page";
import { SentimentCheckPage } from "./features/sentiment-check/pages/sentiment-check-page";
import { Logout } from "./ui/shared/Logout";

const AssessmentRoute = lazy(
  () => import("./features/home/routes/assessment-route")
);
const EditingRoute = lazy(() => import("./ui/app/editing-route"));
const WorkRoute = lazy(() => import("./ui/app/work-route"));
const CharterRoute = lazy(
  () => import("./features/group-charter/routes/charter-route")
);
const FeedbackRoute = lazy(
  () => import("./features/feedback/routes/feedback-route")
);
const FeedbackWorkRoute = lazy(
  () => import("./features/feedback/routes/feedback-work-route")
);
const GroupPage = lazy(() => import("./features/group/group-page"));
const HomeRoute = lazy(() => import("./features/home/routes/home-route"));
const InstructionRoute = lazy(
  () => import("./features/home/routes/instruction-route")
);
const PeerEvaluationRoute = lazy(
  () => import("./features/peer-eval/routes/peer-evaluation-route")
);
const RubricRoute = lazy(() => import("./features/rubric/routes/rubric-route"));

const SocraticRoute = lazy(() => import("./features/socratic/socratic-route"));

export const routes = (
  <Routes>
    <Route
      path="/"
      element={<NoMatchError />}
      errorElement={<NoMatchError />}
    />
    <Route path="/logout" element={<Logout />} />
    <Route path="/goodbye" element={<UnauthorizedError />} />
    <Route path="/:tenant/:assessmentId" element={<AssessmentRoute />}>
      <Route path="home" element={<HomeRoute />}>
        <Route index element={<Preparation />} />
        <Route path="taskboard" element={<ProjectManagementPage />} />
        <Route path="writing" element={<WritingPage />} />
        <Route path="insights" element={<StudentInsights />} />
        <Route path="peer" element={<PeerOverviewPageWithData />} />
        <Route path="sentiment-check" element={<SentimentCheckPage />} />
        <Route path="feedback" element={<FeedbackSummaryPage />} />
      </Route>
      <Route path="instructions/:taskKind" element={<InstructionRoute />} />
      <Route path="rubrics/:markerRubricId" element={<RubricRoute />} />
      <Route path="peer-evaluation/" element={<PeerEvaluationRoute />} />
      <Route path="group" element={<GroupPage />} />
      <Route path="work/:workId">
        <Route element={<WorkRoute />}>
          <Route path="editing" element={<EditingRoute />} />
          <Route path="socratic/:answerBlockId" element={<SocraticRoute />} />
        </Route>
        <Route path="feedback" element={<FeedbackWorkRoute />} />
      </Route>
      <Route path="charter/:charterId" element={<CharterRoute />} />
      <Route path="feedback" element={<FeedbackRoute />} />
    </Route>

    <Route path="*" element={<NoMatchError />} />
  </Routes>
);

export function useRootNavigate() {
  const navigate = useNavigate();
  const { tenant, assessmentId } = useParams();

  return useCallback(
    (path: string, options?: NavigateOptions) => {
      navigate(`/${tenant}/${assessmentId}${path}`, options);
    },
    [navigate, tenant, assessmentId]
  );
}

/**
 * Read the TENANT and ASSESSMENT_ID route parameters that are expected to be
 * present the the web-page URL that this hook is rendered in.
 */
export function useAssessmentParams() {
  const { tenant = "", assessmentId = "" } = useParams();

  __GLOBAL_ASSESSMENT_ID.current = assessmentId;
  __GLOBAL_TENANT.current = tenant;

  return { tenant, assessmentId };
}

/**
 * Read the TENANT, ASSESSMENT_ID, and WORK_ID route parameters that are expected to be
 * present the the web-page URL that this hook is rendered in.
 */
export function useWorkParams() {
  const { tenant = "", assessmentId = "", workId = "" } = useParams();

  __GLOBAL_TENANT.current = tenant;
  __GLOBAL_ASSESSMENT_ID.current = assessmentId;

  return { tenant, assessmentId, workId };
}
